import { Box, TextField, Typography } from '@mui/material';
import { ChangeEvent, useContext, useState } from 'react';

import CommentsWrap from '@/components/comments_components/CommentsWrap';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import useAutosave from '@/hooks/useAutosave';
import { QuestionProps } from '@/models/Question';

import SavedIndicator from '../SavedIndicator';
import CharacterCount from '../event_components/CharacterCount';
import { logError } from '@/utils/errorHandlerUtil';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';

function questionHasBeenFilledOut(value: string | null) {
  return value !== null && value.length > 0;
}

function TextTypeQuestions({ question }: { question: QuestionProps }) {
  const { setAlert } = useContext(SnackbarContext);

  const { answers, canEdit, saveApplicationAnswer2 } = useContext(CurrentApplicationContext);

  const questionAnswer = answers[question.id];
  const [currentValue, setCurrentValue] = useState(questionAnswer?.responseString ?? null);
  const [saveFailed, setSaveFailed] = useState(false);

  const saveValue = async (v: string | null): Promise<void> => {
    setSaveFailed(false);
    try {
      await saveApplicationAnswer2(question, v);
    } catch (error) {
      logError(`Error saving response in text type question.`, error);
      setAlert({
        severity: 'error',
        message: 'Error saving response - please try again.',
      });
      setSaveFailed(true);
    }
  };

  const { saved, isSaving, markTouched, triggerSave } = useAutosave(currentValue, saveValue, 1500);

  const handleChanged = (e: ChangeEvent<HTMLInputElement>) => {
    markTouched();

    const val = e.target.value === '' ? null : e.target.value;
    setCurrentValue(val);
  };

  const maxChars = question.maxCharacters ?? 4000;
  const normalizedQuestionType = question.questionType.trim().toLowerCase();
  const isSingleLineTextbox = normalizedQuestionType === 'text';

  return (
    <Box
      display='flex'
      width='100%'
      justifyContent='space-between'
      alignItems='start'
      id={`text-question-${question.id}`}
    >
      <Box display='flex' flexDirection='row' sx={{ width: '60vw' }}>
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='flex-start'
          gap='4px'
          flexGrow={0}
          sx={{ width: '100%' }}
        >
          <TextField
            id={question.id.toString()}
            label={question.questionText}
            sx={{
              minWidth: '400px',
            }}
            disabled={!canEdit}
            onChange={handleChanged}
            required={question.questionNeeded}
            multiline={!isSingleLineTextbox}
            minRows={isSingleLineTextbox ? 1 : 4}
            error={question.questionNeeded && !questionHasBeenFilledOut(currentValue)}
            value={currentValue ?? ''}
            slotProps={{ htmlInput: { maxLength: maxChars } }}
          />
          {!isSingleLineTextbox && (
            <CharacterCount text={currentValue} max={maxChars} visible={canEdit} />
          )}
          <Typography variant='subtitle3' color='textSecondary'>
            {question.questionNote}
          </Typography>
        </Box>

        <SavedIndicator
          saved={saved}
          isSaving={isSaving.current}
          saveFailed={saveFailed}
          saveFn={triggerSave}
          sx={{ alignSelf: 'center' }}
        />
      </Box>
      <CommentsWrap question={question} />
    </Box>
  );
}

export default TextTypeQuestions;
